import { theme } from 'antd';
import { CreditCardIcon, DeviceGroupsIcon, DevicesIcon, InfoIcon, SearchIcon, TeamIcon } from 'assets/images/svg';
import { groupCredentialsByPhysicalIdentifier, groupPermissionsBySourceForPerson } from 'library/helpers/accessControl';
import { NumberOutlined } from '@ant-design/icons';
import { CREDENTIAL_TYPE_PASSCODE, CREDENTIAL_TYPE_PASSCODE_CARD } from 'config/credentialTypes';
import { formatDisplayName } from 'library/helpers/utility';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAssetGroups } from 'reduxStore/assetGroups/selectors';
import { selectCredentialAccess } from 'reduxStore/credentials/selectors';
import { selectCredentials } from 'reduxStore/credentials/selectors';
import { selectDevices } from 'reduxStore/devices/selectors';
import { selectPeopleBySiteID } from 'reduxStore/people/selectors';
import { selectPeoplePermissions } from 'reduxStore/permissions/selectors';
import { selectTeams } from 'reduxStore/teams/selectors';
import { selectCredentialTypes, selectUserTypes } from 'reduxStore/types/selectors';
import { PersonProfileImage, PopoverTag, TableFilterDropdown } from 'zComponents/atoms';
import { DeletePeopleModal, ManageCredentialsModal, PersonDrawer } from 'zComponents/molecules';
import { Button, Col, Empty, Flex, Input, Row, Table, Typography, Tag, Tooltip } from 'zui/Antd';
const { Text } = Typography;

const PeopleTab = ({ selectedSite }) => {
	// ✅ Hooks
	const {
		token: { fontSizeSM, colorTextDescription, colorPrimary, colorPrimaryBg, fontWeightStrong }
	} = theme.useToken();

	// ✅ Get data from Redux - site-specific people
	const people = useSelector((state) => selectPeopleBySiteID(state, selectedSite));
	const userTypes = useSelector(selectUserTypes);
	const teams = useSelector(selectTeams);
	const permissions = useSelector(selectPeoplePermissions);
	const credentials = useSelector(selectCredentials);
	const credentialAccess = useSelector(selectCredentialAccess);
	const deviceGroups = useSelector(selectAssetGroups);
	const devices = useSelector(selectDevices);
	const credentialTypes = useSelector(selectCredentialTypes);

	// ✅ Filters state
	const [searchText, setSearchText] = useState('');
	const [selectedPersonIDs, setSelectedPersonIDs] = useState([]);
	const [openDrawer, setOpenDrawer] = useState(false);
	const [selectedPerson, setSelectedPerson] = useState(null);
	const [inputValue, setInputValue] = useState('');

	// ✅ Debounced search handler
	const debouncedSearch = _.debounce((searchString) => {
		setSearchText(searchString);
	}, 300);

	// Apply filtering: only search by text (site filtering already done by selectPeopleBySiteID)
	const filteredPeople = useMemo(() => {
		return people.filter((person) => {
			const matchesSearch =
				!searchText.length ||
				[person.firstName, person.lastName, person.displayName, person.email].some((field) =>
					field?.toLowerCase().includes(searchText.toLowerCase())
				);
			return matchesSearch;
		});
	}, [people, searchText]);

	const personNameById = useMemo(() => {
		const map = new Map();
		for (const p of people) map.set(p.personID, p.displayName || '');
		return map;
	}, [people]);

	const credentialTypeMap = useMemo(() => {
		return credentialTypes.reduce((acc, ct) => {
			acc[ct.credentialTypeID] = ct.name;
			return acc;
		}, {});
	}, [credentialTypes]);

	const peopleOptions = useMemo(() => {
		const uniquePersonIDs = [...new Set(people.map((person) => person.personID).filter(Boolean))];
		return uniquePersonIDs
			.map((personID) => ({
				label: personNameById.get(personID) || '',
				value: personID
			}))
			.filter((option) => option.label) // Only include if we have a name
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}, [people, personNameById]);

	// category options
	const categoryOptions = useMemo(() => {
		const uniqueTypeIDs = [...new Set(people.map((p) => p.userTypeID).filter(Boolean))];
		return uniqueTypeIDs
			.map((id) => {
				const type = userTypes.find((t) => t.userTypeID === id);
				return type ? { label: type.name, value: id } : null;
			})
			.filter(Boolean)
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}, [people, userTypes]);

	// teams options
	const teamsOptions = useMemo(() => {
		const allTeamNames = new Set();
		people.forEach((person) => {
			const personTeams = teams.filter((team) => team?.members?.includes(person.personID));
			personTeams.forEach((team) => allTeamNames.add(team.name));
		});
		return Array.from(allTeamNames)
			.map((teamName) => ({ label: teamName, value: teamName }))
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}, [people, teams]);

	// credential type options
	const credentialOptions = useMemo(() => {
		const allCredentialTypes = new Set();
		people.forEach((person) => {
			const personCreds = credentials.filter((cred) => cred.personID === person.personID);
			personCreds.forEach((cred) => {
				const typeName = credentialTypeMap[cred.credentialTypeID] || 'Unknown';
				allCredentialTypes.add(typeName);
			});
		});
		return Array.from(allCredentialTypes)
			.map((typeName) => ({ label: typeName, value: typeName }))
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}, [people, credentials, credentialTypeMap]);

	// access permissions options
	const accessPermissionsOptions = useMemo(() => {
		const labels = new Set();

		people.forEach((person) => {
			const personPermissions = permissions.filter((p) => p.personID === person.personID);
			const displayItems = groupPermissionsBySourceForPerson({
				permissions: personPermissions,
				devices,
				deviceGroups,
				teams
			});

			displayItems.forEach((item) => labels.add(item.label));
		});

		return Array.from(labels)
			.map((label) => ({ label, value: label }))
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [people, permissions, devices, deviceGroups, teams, credentialAccess, credentials]);

	// ✅ Handle input change
	const handleChangeInput = (e) => {
		const value = e.target.value;
		setInputValue(value);
		debouncedSearch(value);
	};

	// ✅ Table row selection
	const rowSelection = {
		selectedRowKeys: selectedPersonIDs,
		onChange: setSelectedPersonIDs
	};

	// ✅ Table columns definition
	const columns = [
		{
			title: 'PERSON',
			dataIndex: 'displayName',
			key: 'name',
			width: 350,
			render: (_, record) => {
				const name = formatDisplayName(record)?.displayName;
				const email = record.email || '---';
				return (
					<Flex align='center' gap={12}>
						<PersonProfileImage
							size={42}
							photoUrl={record.photoUrl || undefined}
							name={record?.displayName}
							entityName={'People'}
							shape={'circle'}
							edit={false}
						/>
						<Flex vertical>
							<Text strong style={{ lineHeight: '22px' }}>
								{name}
							</Text>
							<Text style={{ fontSize: fontSizeSM, lineHeight: '20px' }}>{email}</Text>
						</Flex>
					</Flex>
				);
			},
			sorter: (a, b) => a.displayName.localeCompare(b.displayName),
			onFilter: (value, record) => record.personID === value,
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={peopleOptions} placeholder='Search people' />
			)
		},
		{
			title: 'TEAMS',
			dataIndex: 'teams',
			key: 'teams',
			width: 200,
			render: (_, record) => {
				const teamsData = teams.filter((team) => team?.members?.includes(record.personID));
				const count = teamsData.length;

				if (count === 0) return <Text type='secondary' style={{ fontSize: 12 }}>—</Text>;

				return (
					<PopoverTag
						icon={TeamIcon}
						count={count}
						singular='team'
						plural='teams'
						items={teamsData.map((team) => ({ key: team.teamID, label: team.name }))}
					/>
				);
			},
			sorter: (a, b) => {
				const aTeams = teams.filter((team) => team?.members?.includes(a.personID));
				const bTeams = teams.filter((team) => team?.members?.includes(b.personID));

				// Primary sort: by number of teams
				const lengthDiff = aTeams.length - bTeams.length;
				if (lengthDiff !== 0) return lengthDiff;

				// Secondary sort: by first team name alphabetically
				const aFirstTeam = aTeams[0]?.name || '';
				const bFirstTeam = bTeams[0]?.name || '';
				return aFirstTeam.localeCompare(bFirstTeam, undefined, { sensitivity: 'base' });
			},
			onFilter: (value, record) => {
				const recordTeams = teams.filter((team) => team?.members?.includes(record.personID));
				return recordTeams.some((team) => team.name === value);
			},
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={teamsOptions} placeholder='Search teams' />
			)
		},
		{
			title: 'TYPE',
			dataIndex: 'userTypeID',
			key: 'userType',
			width: 200,
			render: (userTypeID) => {
				const userType = userTypes.find((type) => type.userTypeID === userTypeID);
				const userTypeName = userType ? userType.name : '-';
				const isZezamiiUser = userTypeName === 'Zezamii User';

				return userTypeName !== '-' ? (
					<Tag
						color={isZezamiiUser ? colorPrimaryBg : '#E2FFFF'}
						style={{
							color: isZezamiiUser ? colorPrimary : '#2B9C9C',
							border: 'none',
							fontWeight: fontWeightStrong
						}}
					>
						{userTypeName}
					</Tag>
				) : (
					'-'
				);
			},
			sorter: (a, b) => {
				const aType = userTypes.find((type) => type.userTypeID === a.userTypeID)?.name || '';
				const bType = userTypes.find((type) => type.userTypeID === b.userTypeID)?.name || '';
				return aType.localeCompare(bType);
			},
			onFilter: (value, record) => record.userTypeID === value,
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={categoryOptions} placeholder='Search user types' />
			)
		},

		{
			title: 'CREDENTIAL',
			dataIndex: 'credential',
			key: 'credential',
			width: 250,
			render: (_, record) => {
				const personCreds = groupCredentialsByPhysicalIdentifier(
					credentials.filter((cred) => cred.personID === record.personID)
				);
				const cards = personCreds.filter((c) => c.credentialTypeID === CREDENTIAL_TYPE_PASSCODE_CARD);
				const passcodes = personCreds.filter((c) => c.credentialTypeID === CREDENTIAL_TYPE_PASSCODE);

				if (cards.length === 0 && passcodes.length === 0) return <Text type='secondary' style={{ fontSize: 12 }}>—</Text>;

				return (
					<Flex gap={4} wrap='wrap'>
						{cards.length > 0 && (
							<PopoverTag
								icon={CreditCardIcon}
								count={cards.length}
								singular='card'
								plural='cards'
								items={cards.map((c) => ({ key: c.credentialID, label: c.name || c.value || 'Card' }))}
							/>
						)}
						{passcodes.length > 0 && (
							<PopoverTag
								icon={NumberOutlined}
								count={passcodes.length}
								singular='passcode'
								plural='passcodes'
								items={passcodes.map((p) => ({ key: p.credentialID, label: p.name || 'Passcode' }))}
							/>
						)}
					</Flex>
				);
			},
			sorter: (a, b) => {
				const aCreds = credentials.filter((cred) => cred.personID === a.personID).length;
				const bCreds = credentials.filter((cred) => cred.personID === b.personID).length;
				return aCreds - bCreds;
			},
			onFilter: (value, record) => {
				const recordCreds = credentials.filter((cred) => cred.personID === record.personID);
				return recordCreds.some((cred) => (credentialTypeMap[cred.credentialTypeID] || 'Unknown') === value);
			},
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={credentialOptions} placeholder='Search credential types' />
			)
		},
		{
			title: 'ACCESS PERMISSIONS',
			dataIndex: 'accessPermissions',
			key: 'accessPermissions',
			width: 300,
			render: (_, record) => {
				const personPermissions = permissions.filter((p) => p.personID === record.personID);
				const displayItems = groupPermissionsBySourceForPerson({
					permissions: personPermissions,
					devices,
					deviceGroups,
					teams
				});

				const directDevices = displayItems.filter((item) => !item.assetGroupID && !item.teamID);
				const groupItems = displayItems.filter((item) => item.assetGroupID && !item.teamID);
				const teamItems = displayItems.filter((item) => item.teamID);

				const uniqueTeams = [];
				const seenTeamIDs = new Set();
				teamItems.forEach((item) => {
					if (!seenTeamIDs.has(item.teamID)) {
						seenTeamIDs.add(item.teamID);
						const team = teams.find((t) => t.teamID === item.teamID);
						if (team) uniqueTeams.push(team);
					}
				});

				if (directDevices.length === 0 && groupItems.length === 0 && uniqueTeams.length === 0) return <Text type='secondary' style={{ fontSize: 12 }}>—</Text>;

				return (
					<Flex gap={4} wrap='wrap'>
						{directDevices.length > 0 && (
							<PopoverTag
								icon={DevicesIcon}
								count={directDevices.length}
								singular='device'
								plural='devices'
								items={directDevices.map((item, i) => ({ key: i, label: item.label }))}
							/>
						)}
						{groupItems.length > 0 && (
							<PopoverTag
								icon={DeviceGroupsIcon}
								count={groupItems.length}
								singular='group'
								plural='groups'
								items={groupItems.map((item, i) => ({ key: i, label: item.label }))}
							/>
						)}
						{uniqueTeams.length > 0 && (
							<PopoverTag
								icon={TeamIcon}
								count={uniqueTeams.length}
								singular='team'
								plural='teams'
								items={uniqueTeams.map((team) => ({ key: team.teamID, label: team.name }))}
							/>
						)}
					</Flex>
				);
			},
			onFilter: (value, record) => {
				const personPermissions = permissions.filter((p) => p.personID === record.personID);
				const displayItems = groupPermissionsBySourceForPerson({
					permissions: personPermissions,
					devices,
					deviceGroups,
					teams
				});
				return displayItems.some((item) => item.label === value);
			},
			sorter: (a, b) => {
				const aCount = permissions.filter((p) => p.personID === a.personID).length;
				const bCount = permissions.filter((p) => p.personID === b.personID).length;
				return aCount - bCount;
			},
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={accessPermissionsOptions} placeholder='Search permissions' />
			)
		},
		{
			title: 'ACTIONS',
			dataIndex: 'actions',
			align: 'center',
			fixed: 'right',
			width: 150,
			render: (_, record) => (
				<Flex gap={8} justify='center' onClick={(e) => e.stopPropagation()}>
					<Tooltip title='View Person Details' placement='top'>
						<Button
							type='text'
							icon={<InfoIcon color='currentColor' />}
							size='small'
							onClick={() => {
								setSelectedPerson(record.personID);
								setOpenDrawer(true);
							}}
						/>
					</Tooltip>
					<ManageCredentialsModal personID={record.personID} />
					<DeletePeopleModal personIDs={[record.personID]} />
				</Flex>
			)
		}
	];

	return (
		<Row>
			<Col span={24} style={{ width: '100%', marginBottom: 24 }}>
				<Row justify='space-between'>
					<Col span={10}>
						<Input
							placeholder='Search People'
							value={inputValue}
							allowClear
							onChange={(e) => handleChangeInput(e)}
							onClear={() => setInputValue('')}
							suffix={<SearchIcon color={colorTextDescription} />}
						/>
					</Col>
				</Row>
			</Col>
			<Col span={24}>
				<Table
					rowSelection={rowSelection}
					dataSource={filteredPeople.map((person) => ({ ...person, key: person.personID }))}
					columns={columns}
					size='small'
					locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No people found in this site.' /> }}
					onRow={(record) => ({
						onClick: () => {
							setSelectedPerson(record.personID);
							setOpenDrawer(true);
						},
						style: { cursor: 'pointer' }
					})}
					scroll={{ x: 1500 }}
					pagination={{
						defaultPageSize: 10,
						showQuickJumper: true,
						showSizeChanger: false
					}}
				/>
			</Col>

			<PersonDrawer personID={selectedPerson} visible={openDrawer} setVisible={setOpenDrawer} />
		</Row>
	);
};

export default PeopleTab;
